/**
 * Labour Shift Config — Settings → Labour → Shift Model
 * Governs: standard hours, OT trigger, daily cap, block after cap.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Save, RefreshCw, Info } from 'lucide-react';

const DEFAULTS = {
  shift_start_time: '09:00',
  shift_end_time: '18:00',
  standard_shift_hours: 9,
  max_daily_hours: 15,
  ot_block_hours: 3,
  ot_multiplier: 1.5,
  block_punch_after_max: true,
};

export default function LabourShiftConfig() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/settings/labour-shift-config').then(d => setCfg({ ...DEFAULTS, ...d })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value);
    setCfg(c => ({ ...c, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/labour-shift-config', cfg);
      toast.success('Shift configuration saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const Field = ({ label, k, type = 'number', help, min, max, step = 0.5 }) => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {type === 'checkbox' ? (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg[k]} onChange={upd(k)}
            className="w-4 h-4 rounded" data-testid={`shift-cfg-${k}`} />
          <span className="text-sm text-slate-700">Enabled</span>
        </label>
      ) : (
        <input type={type} value={cfg[k] ?? ''} onChange={upd(k)}
          min={min} max={max} step={type === 'number' ? step : undefined}
          className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
          data-testid={`shift-cfg-${k}`} />
      )}
      {help && <p className="text-[10px] text-slate-400 mt-1">{help}</p>}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-6 space-y-6">
      <button onClick={() => nav('/app/settings')} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
        <ArrowLeft size={13} /> Settings
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#1F7A6E]/10 rounded-sm flex items-center justify-center">
          <Clock size={20} className="text-[#1F7A6E]" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl">Labour Shift Model</h1>
          <p className="text-sm text-slate-500 mt-0.5">Defines how hours are converted to shifts and overtime pay.</p>
        </div>
      </div>

      {/* Model summary */}
      <div className="bg-[#1F7A6E]/5 border border-[#1F7A6E]/20 rounded-sm px-4 py-3 flex items-start gap-2 text-xs text-[#1F7A6E]">
        <Info size={14} className="shrink-0 mt-0.5" />
        <div>
          <strong>Current model:</strong> {cfg.standard_shift_hours}h on site = 1 shift.
          Every {cfg.ot_block_hours}h OT block = +0.5 shift at {cfg.ot_multiplier}×.
          Max {cfg.max_daily_hours}h/day = 2 shifts.
          {cfg.block_punch_after_max ? ' Punch-in blocked after cap.' : ' Cap is informational only.'}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><RefreshCw size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
          <div className="p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Shift Times</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Shift Start Time" k="shift_start_time" type="time" step={undefined} help="Default site start time (display only)" />
              <Field label="Shift End Time" k="shift_end_time" type="time" step={undefined} help="Default site end time (display only)" />
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Pay Calculation</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Standard Shift Hours" k="standard_shift_hours" min={4} max={12} step={1}
                help="Hours on site that equal 1 full shift (min 4, max 12)" />
              <Field label="OT Block Hours" k="ot_block_hours" min={1} max={4} step={1}
                help="Every N overtime hours = +0.5 shift (e.g. 3h OT = +0.5 shift)" />
              <Field label="OT Multiplier" k="ot_multiplier" min={1} max={3} step={0.25}
                help="Multiplier for overtime pay (1.5 = time-and-a-half)" />
              <Field label="Max Daily Hours" k="max_daily_hours" min={8} max={20} step={1}
                help="Hard cap per day — punch blocked after this many hours" />
            </div>
          </div>
          <div className="p-5">
            <Field label="Block punch-in after max hours" k="block_punch_after_max" type="checkbox"
              help="When enabled, kiosk and mobile punch-in returns 400 once the daily cap is reached." />
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving || loading}
        className="flex items-center gap-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white px-6 py-2.5 rounded-sm text-sm font-bold disabled:opacity-50"
        data-testid="shift-cfg-save-btn">
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
        Save Configuration
      </button>
    </div>
  );
}
